import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  AlertTriangle,
  Trash2,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Loader2,
  FileText,
  CreditCard,
  Receipt,
  RotateCcw,
  Database,
  Building2,
  ChevronDown,
} from 'lucide-react';

/**
 * BillingDataReset - Admin component for resetting billing data in test mode
 *
 * Allows superadmins to:
 * - Preview what billing data would be deleted
 * - Select specific data types to include/exclude
 * - Reset billing data for a specific organization or platform-wide
 * - Requires typing confirmation phrase for safety
 */
const BillingDataReset = ({ currentUser }) => {
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Organization selection
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);

  // Data type selections
  const [includeInvoices, setIncludeInvoices] = useState(true);
  const [includePayments, setIncludePayments] = useState(true);
  const [includeReceipts, setIncludeReceipts] = useState(true);
  const [includeRefunds, setIncludeRefunds] = useState(true);
  const [includePaymentMethods, setIncludePaymentMethods] = useState(true);
  const [includeTaxTransactions, setIncludeTaxTransactions] = useState(true);
  const [includeSubscriptions, setIncludeSubscriptions] = useState(true);
  const [clearExternalIds, setClearExternalIds] = useState(true);

  // Preview and confirmation
  const [preview, setPreview] = useState(null);
  const [confirmationInput, setConfirmationInput] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Execution result
  const [result, setResult] = useState(null);

  const isSuperAdmin = currentUser?.role === 'superadmin';

  useEffect(() => {
    if (isSuperAdmin) {
      fetchOrganizations();
    }
  }, [isSuperAdmin]);

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get(
        `${getApiBase()}/admin/billing/organizations?limit=100`,
        { headers: getAuthHeaders() }
      );
      setOrganizations(response.data.organizations || []);
    } catch (err) {
      console.error('Error fetching organizations:', err);
    }
  };

  const handlePreview = async () => {
    setError('');
    setSuccess('');
    setPreviewing(true);
    setPreview(null);

    try {
      const response = await axios.post(
        `${getApiBase()}/admin/billing/reset/preview`,
        {
          organization_id: selectedOrgId || null,
          include_invoices: includeInvoices,
          include_payments: includePayments,
          include_receipts: includeReceipts,
          include_refunds: includeRefunds,
          include_payment_methods: includePaymentMethods,
          include_tax_transactions: includeTaxTransactions,
          include_subscriptions: includeSubscriptions,
          clear_external_ids: clearExternalIds,
        },
        { headers: getAuthHeaders() }
      );
      setPreview(response.data);
    } catch (err) {
      console.error('Error previewing reset:', err);
      setError(err.response?.data?.detail || 'Failed to preview reset');
    } finally {
      setPreviewing(false);
    }
  };

  const handleExecute = async () => {
    if (!preview) return;

    // Validate confirmation phrase
    if (confirmationInput !== preview.required_confirmation_phrase) {
      setError(`Please type "${preview.required_confirmation_phrase}" to confirm`);
      return;
    }

    setError('');
    setExecuting(true);

    try {
      const response = await axios.post(
        `${getApiBase()}/admin/billing/reset/execute`,
        {
          organization_id: selectedOrgId || null,
          include_invoices: includeInvoices,
          include_payments: includePayments,
          include_receipts: includeReceipts,
          include_refunds: includeRefunds,
          include_payment_methods: includePaymentMethods,
          include_tax_transactions: includeTaxTransactions,
          include_subscriptions: includeSubscriptions,
          clear_external_ids: clearExternalIds,
          confirmation_phrase: confirmationInput,
        },
        { headers: getAuthHeaders() }
      );
      setResult(response.data);
      setSuccess('Billing data reset completed successfully');
      setShowConfirmModal(false);
      setPreview(null);
      setConfirmationInput('');
    } catch (err) {
      console.error('Error executing reset:', err);
      setError(err.response?.data?.detail || 'Failed to execute reset');
    } finally {
      setExecuting(false);
    }
  };

  const filteredOrganizations = organizations.filter(org =>
    org.organization?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.organization?.id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOrg = organizations.find(
    org => org.organization?.id === selectedOrgId
  )?.organization;

  if (!isSuperAdmin) {
    return (
      <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-center gap-2 text-yellow-800">
          <AlertCircle className="w-5 h-5" />
          <span>Only superadmins can reset billing data.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
          <RotateCcw className="w-6 h-6 text-red-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Reset Billing Data</h3>
          <p className="text-sm text-gray-500">
            Clear test billing data before switching to production mode
          </p>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium">Warning: Destructive Operation</p>
            <p className="text-red-700 text-sm mt-1">
              This operation permanently deletes billing data. It cannot be undone.
              Only use this to clear sandbox/test data before switching to production.
            </p>
          </div>
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <p className="text-green-800">{success}</p>
        </div>
      )}

      {/* Organization Selection */}
      <div className="border rounded-lg p-4 bg-white">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Scope
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowOrgDropdown(!showOrgDropdown)}
            className="w-full flex items-center justify-between px-4 py-2 border rounded-md bg-white text-left"
          >
            <div className="flex items-center gap-2">
              {selectedOrgId ? (
                <>
                  <Building2 className="w-4 h-4 text-gray-500" />
                  <span>{selectedOrg?.name || 'Unknown Organization'}</span>
                </>
              ) : (
                <>
                  <Database className="w-4 h-4 text-red-500" />
                  <span className="text-red-600 font-medium">Platform-wide (All Organizations)</span>
                </>
              )}
            </div>
            <ChevronDown className="w-4 h-4 text-gray-500" />
          </button>

          {showOrgDropdown && (
            <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
              <div className="p-2 border-b">
                <input
                  type="text"
                  placeholder="Search organizations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedOrgId('');
                  setShowOrgDropdown(false);
                  setPreview(null);
                }}
                className={`w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 ${
                  !selectedOrgId ? 'bg-red-50' : ''
                }`}
              >
                <Database className="w-4 h-4 text-red-500" />
                <span className="text-red-600">Platform-wide (All Organizations)</span>
              </button>
              {filteredOrganizations.map((org) => (
                <button
                  key={org.organization?.id}
                  type="button"
                  onClick={() => {
                    setSelectedOrgId(org.organization?.id);
                    setShowOrgDropdown(false);
                    setPreview(null);
                  }}
                  className={`w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 ${
                    selectedOrgId === org.organization?.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <Building2 className="w-4 h-4 text-gray-500" />
                  <span>{org.organization?.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Data Type Selection */}
      <div className="border rounded-lg p-4 bg-white">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Data to Delete
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={includeInvoices}
              onChange={(e) => setIncludeInvoices(e.target.checked)}
              className="rounded text-blue-600"
            />
            <FileText className="w-4 h-4 text-gray-500" />
            <span className="text-sm">Invoices</span>
          </label>

          <label className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={includePayments}
              onChange={(e) => setIncludePayments(e.target.checked)}
              className="rounded text-blue-600"
            />
            <CreditCard className="w-4 h-4 text-gray-500" />
            <span className="text-sm">Payments</span>
          </label>

          <label className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={includeReceipts}
              onChange={(e) => setIncludeReceipts(e.target.checked)}
              className="rounded text-blue-600"
            />
            <Receipt className="w-4 h-4 text-gray-500" />
            <span className="text-sm">Receipts</span>
          </label>

          <label className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={includeRefunds}
              onChange={(e) => setIncludeRefunds(e.target.checked)}
              className="rounded text-blue-600"
            />
            <RotateCcw className="w-4 h-4 text-gray-500" />
            <span className="text-sm">Refunds & Credit Notes</span>
          </label>

          <label className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={includePaymentMethods}
              onChange={(e) => setIncludePaymentMethods(e.target.checked)}
              className="rounded text-blue-600"
            />
            <CreditCard className="w-4 h-4 text-gray-500" />
            <span className="text-sm">Payment Methods</span>
          </label>

          <label className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={includeTaxTransactions}
              onChange={(e) => setIncludeTaxTransactions(e.target.checked)}
              className="rounded text-blue-600"
            />
            <Database className="w-4 h-4 text-gray-500" />
            <span className="text-sm">Tax Transactions</span>
          </label>

          <label className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 cursor-pointer col-span-2 bg-yellow-50 border-yellow-200">
            <input
              type="checkbox"
              checked={includeSubscriptions}
              onChange={(e) => setIncludeSubscriptions(e.target.checked)}
              className="rounded text-yellow-600"
            />
            <FileText className="w-4 h-4 text-yellow-600" />
            <span className="text-sm text-yellow-800">Subscriptions (Active package assignments)</span>
          </label>
        </div>

        <div className="mt-4 pt-4 border-t">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={clearExternalIds}
              onChange={(e) => setClearExternalIds(e.target.checked)}
              className="rounded text-blue-600"
            />
            <span className="text-sm">
              Clear external IDs (Stripe, Xero, TaxJar) from billing profiles
            </span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-6">
            This preserves billing profiles but removes integration links.
            New IDs will be created when the organization next interacts with each service.
          </p>
        </div>
      </div>

      {/* Preview Button */}
      <button
        onClick={handlePreview}
        disabled={previewing}
        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md disabled:opacity-50"
      >
        {previewing ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <RefreshCw className="w-4 h-4" />
        )}
        Preview Changes
      </button>

      {/* Preview Results */}
      {preview && (
        <div className="border rounded-lg p-4 bg-white">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-500" />
            Preview: Records to be Deleted
          </h4>

          <div className="grid grid-cols-3 gap-4 mb-4">
            {preview.counts?.invoices !== undefined && (
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-2xl font-bold text-gray-900">{preview.counts.invoices}</div>
                <div className="text-sm text-gray-500">Invoices</div>
              </div>
            )}
            {preview.counts?.payments !== undefined && (
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-2xl font-bold text-gray-900">{preview.counts.payments}</div>
                <div className="text-sm text-gray-500">Payments</div>
              </div>
            )}
            {preview.counts?.receipts !== undefined && (
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-2xl font-bold text-gray-900">{preview.counts.receipts}</div>
                <div className="text-sm text-gray-500">Receipts</div>
              </div>
            )}
            {preview.counts?.refunds !== undefined && (
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-2xl font-bold text-gray-900">{preview.counts.refunds}</div>
                <div className="text-sm text-gray-500">Refunds</div>
              </div>
            )}
            {preview.counts?.payment_methods !== undefined && (
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-2xl font-bold text-gray-900">{preview.counts.payment_methods}</div>
                <div className="text-sm text-gray-500">Payment Methods</div>
              </div>
            )}
            {preview.counts?.tax_transactions !== undefined && (
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-2xl font-bold text-gray-900">{preview.counts.tax_transactions}</div>
                <div className="text-sm text-gray-500">Tax Transactions</div>
              </div>
            )}
            {preview.counts?.subscriptions !== undefined && (
              <div className="p-3 bg-yellow-50 rounded border border-yellow-200">
                <div className="text-2xl font-bold text-yellow-900">{preview.counts.subscriptions}</div>
                <div className="text-sm text-yellow-700">Subscriptions</div>
              </div>
            )}
          </div>

          {preview.counts?.billing_profiles_to_update > 0 && (
            <p className="text-sm text-gray-600 mb-4">
              {preview.counts.billing_profiles_to_update} billing profile(s) will have external IDs cleared
            </p>
          )}

          <div className="p-3 bg-red-50 rounded mb-4">
            <div className="text-lg font-bold text-red-900">
              Total: {preview.total_records_to_delete} records will be deleted
            </div>
          </div>

          {preview.warnings?.length > 0 && (
            <div className="mb-4">
              {preview.warnings.map((warning, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm text-yellow-700 mb-1">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{warning}</span>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={() => setShowConfirmModal(true)}
            disabled={preview.total_records_to_delete === 0}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            Delete {preview.total_records_to_delete} Records
          </button>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && preview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 text-red-600 mb-4">
              <AlertTriangle className="w-6 h-6" />
              <h3 className="text-lg font-semibold">Confirm Billing Data Reset</h3>
            </div>

            <div className="space-y-3 text-sm text-gray-600 mb-4">
              <p>You are about to permanently delete:</p>
              <ul className="list-disc ml-4 space-y-1">
                {preview.counts?.invoices > 0 && <li>{preview.counts.invoices} invoices</li>}
                {preview.counts?.payments > 0 && <li>{preview.counts.payments} payments</li>}
                {preview.counts?.receipts > 0 && <li>{preview.counts.receipts} receipts</li>}
                {preview.counts?.refunds > 0 && <li>{preview.counts.refunds} refunds</li>}
                {preview.counts?.payment_methods > 0 && <li>{preview.counts.payment_methods} payment methods</li>}
                {preview.counts?.tax_transactions > 0 && <li>{preview.counts.tax_transactions} tax transactions</li>}
                {preview.counts?.subscriptions > 0 && <li className="text-yellow-700 font-medium">{preview.counts.subscriptions} subscriptions</li>}
              </ul>

              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                <p className="font-medium text-yellow-800">This action cannot be undone!</p>
              </div>

              <p>
                Type <code className="bg-gray-100 px-2 py-0.5 rounded font-mono text-red-600">
                  {preview.required_confirmation_phrase}
                </code> to confirm:
              </p>
              <input
                type="text"
                value={confirmationInput}
                onChange={(e) => setConfirmationInput(e.target.value)}
                className="w-full px-3 py-2 border rounded-md font-mono"
                placeholder={preview.required_confirmation_phrase}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmationInput('');
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleExecute}
                disabled={executing || confirmationInput !== preview.required_confirmation_phrase}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
              >
                {executing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Delete Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result Display */}
      {result && result.success && (
        <div className="border rounded-lg p-4 bg-green-50">
          <h4 className="font-medium text-green-900 mb-3 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Reset Complete
          </h4>
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(result.deleted_counts || {}).map(([key, count]) => (
              <div key={key} className="text-sm">
                <span className="font-medium">{count}</span>{' '}
                <span className="text-gray-600">{key.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-600 mt-3">
            Executed by {result.executed_by} at {new Date(result.executed_at).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
};

export default BillingDataReset;
